import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle, WarningCircle, Clock, Ticket, CalendarBlank, Hourglass, Prohibit } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_META = {
  trial: { label: 'Free Trial', color: 'bg-blue-100 text-blue-700', icon: Clock },
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  pending_approval: { label: 'Awaiting Approval', color: 'bg-amber-100 text-amber-700', icon: Hourglass },
  pending_payment: { label: 'Payment Due', color: 'bg-amber-100 text-amber-700', icon: WarningCircle },
  paused: { label: 'Paused', color: 'bg-orange-100 text-orange-700', icon: WarningCircle },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700', icon: WarningCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: Prohibit },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700', icon: WarningCircle },
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : null);

const SellerSubscriptionCard = () => {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [showPlans, setShowPlans] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponPreview, setCouponPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [me, p] = await Promise.all([
        axios.get(`${API_URL}/api/subscription/me`),
        axios.get(`${API_URL}/api/subscription-plans`),
      ]);
      setSub(me.data);
      setPlans(p.data?.plans || []);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const validateCoupon = async () => {
    if (!selectedPlanId || !couponCode.trim()) return;
    try {
      const res = await axios.post(`${API_URL}/api/subscription/validate-coupon`, { plan_id: selectedPlanId, code: couponCode.trim().toUpperCase() });
      setCouponPreview(res.data);
      toast.success(`Coupon applied: -${res.data.discount}`);
    } catch (e) {
      setCouponPreview(null);
      toast.error(e.response?.data?.detail || 'Invalid coupon');
    }
  };

  const subscribe = async () => {
    if (!selectedPlanId) return toast.error('Select a plan first');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/subscription/subscribe`, {
        plan_id: selectedPlanId,
        coupon_code: couponCode.trim().toUpperCase() || undefined,
      });
      setSub(res.data);
      setShowPlans(false);
      setSelectedPlanId(null);
      setCouponCode('');
      setCouponPreview(null);
      toast.success(res.data.status === 'active' ? 'Subscribed! Plan active.' : 'Request submitted — awaiting admin approval.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Subscription failed');
    } finally { setSubmitting(false); }
  };

  if (!sub) return null;
  const meta = STATUS_META[sub.status] || STATUS_META.expired;
  const Icon = meta.icon;
  const trialDaysLeft = sub.status === 'trial' ? daysUntil(sub.trial_ends_at) : null;
  const periodDaysLeft = sub.status === 'active' ? daysUntil(sub.period_ends_at) : null;
  const needsAction = ['expired', 'paused', 'pending_payment', 'rejected'].includes(sub.status);
  // Resolve start/end dates for the visible duration row (works for trial + active + pending)
  const startIso = sub.status === 'trial' ? sub.trial_starts_at : sub.period_starts_at;
  const endIso = sub.status === 'trial' ? sub.trial_ends_at : sub.period_ends_at;
  const startDate = fmtDate(startIso);
  const endDate = fmtDate(endIso);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="seller-subscription-card">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">My Subscription</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${meta.color}`} data-testid="subscription-status-badge">
              <Icon size={12} weight="fill" /> {meta.label}
            </span>
            {sub.plan_snapshot && <span className="text-xs text-slate-500">· {sub.plan_snapshot.name}</span>}
          </div>
        </div>
        <Button size="sm" onClick={() => setShowPlans(s => !s)} className={`rounded-lg h-8 text-xs ${needsAction ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-slate-800 hover:bg-slate-900'}`} data-testid="toggle-plans-btn">
          {needsAction ? 'Subscribe' : (showPlans ? 'Hide plans' : 'Change plan')}
        </Button>
      </div>

      {(startDate || endDate) && ['trial', 'active', 'pending_approval'].includes(sub.status) && (
        <div className="flex flex-wrap gap-4 text-xs text-slate-600 mb-2" data-testid="sub-duration-row">
          {startDate && (
            <div className="flex items-center gap-1.5">
              <CalendarBlank size={13} className="text-emerald-500" />
              <span className="text-slate-500">Start</span>
              <span className="font-semibold text-slate-800" data-testid="sub-start-date">{startDate}</span>
            </div>
          )}
          {endDate && (
            <div className="flex items-center gap-1.5">
              <CalendarBlank size={13} className="text-amber-500" />
              <span className="text-slate-500">End</span>
              <span className="font-semibold text-slate-800" data-testid="sub-end-date">{endDate}</span>
            </div>
          )}
        </div>
      )}

      {trialDaysLeft !== null && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700" data-testid="trial-days-left">
          <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}</strong> left in your free trial. Subscribe to a plan to keep your listings live after the trial.
        </div>
      )}
      {periodDaysLeft !== null && (
        <div className="text-xs text-slate-500" data-testid="period-renew-info">Plan renews in {periodDaysLeft} day{periodDaysLeft !== 1 ? 's' : ''}</div>
      )}
      {sub.status === 'pending_approval' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="pending-approval-banner">
          <Hourglass size={14} weight="fill" className="inline mr-1 text-amber-600" />
          Your subscription request is awaiting <strong>admin approval</strong>. Your listings stay paused until approved.
        </div>
      )}
      {sub.status === 'pending_payment' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="pending-payment-banner">
          Amount due: <strong>{sub.amount_due} {sub.plan_snapshot?.currency}</strong>. Admin will activate your subscription once payment is received.
        </div>
      )}
      {sub.status === 'rejected' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="rejected-banner">
          <Prohibit size={14} weight="fill" className="inline mr-1" />
          Your subscription request was rejected{sub.rejection_reason ? <>: <em>&ldquo;{sub.rejection_reason}&rdquo;</em></> : '.'} You can re-apply below.
        </div>
      )}
      {sub.status === 'expired' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="expired-banner">
          Your trial / subscription has ended. Your live listings have been paused. Subscribe to re-activate them.
        </div>
      )}
      {sub.status === 'paused' && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700" data-testid="paused-banner">
          Your subscription is paused by admin. Please contact admin to reactivate.
        </div>
      )}

      {showPlans && (
        <div className="mt-4 border-t border-slate-200 pt-4" data-testid="plans-picker">
          <h4 className="text-sm font-semibold text-slate-900 mb-3">Choose a plan</h4>
          {plans.length === 0 && <p className="text-xs text-slate-500">No plans available yet. Please check back later.</p>}
          <div className="space-y-2">
            {plans.map(p => (
              <label key={p.plan_id} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedPlanId === p.plan_id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`} data-testid={`pick-plan-${p.plan_id}`}>
                <input type="radio" name="plan" checked={selectedPlanId === p.plan_id} onChange={() => { setSelectedPlanId(p.plan_id); setCouponPreview(null); }} className="mt-1" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 text-sm">{p.name}</span>
                    {p.discount_type !== 'none' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-700">{p.discount_value}{p.discount_type === 'percentage' ? '%' : ''} off</span>}
                  </div>
                  {p.description && <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>}
                  <p className="text-xs text-slate-700 mt-1">
                    <strong>{p.price} {p.currency}</strong> / {p.billing_period}
                    {p.free_trial_days > 0 && <span className="ml-2 text-blue-600">· {p.free_trial_days}d trial</span>}
                    {p.max_listings > 0 && <span className="ml-2 text-slate-500">· up to {p.max_listings} listings</span>}
                  </p>
                </div>
              </label>
            ))}
          </div>

          {selectedPlanId && (
            <div className="mt-3 flex gap-2 items-center flex-wrap" data-testid="coupon-row">
              <Ticket size={14} className="text-slate-400" />
              <Input value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="Coupon code (optional)" className="flex-1 min-w-[140px] h-9 text-sm" data-testid="coupon-input" />
              <Button size="sm" variant="outline" onClick={validateCoupon} disabled={!couponCode.trim()} className="h-9 rounded-lg text-xs" data-testid="apply-coupon-btn">Apply</Button>
              {couponPreview && <span className="text-xs text-emerald-600 font-semibold" data-testid="coupon-preview">−{couponPreview.discount} → Pay {couponPreview.amount_due}</span>}
            </div>
          )}
          <div className="mt-4 flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setShowPlans(false)} className="text-slate-600 h-9 rounded-lg text-xs">Cancel</Button>
            <Button size="sm" onClick={subscribe} disabled={!selectedPlanId || submitting} className="bg-emerald-500 hover:bg-emerald-600 text-white h-9 rounded-lg text-xs" data-testid="confirm-subscribe-btn">
              {submitting ? 'Submitting...' : 'Subscribe'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerSubscriptionCard;
